import { useEffect, useState } from "react";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DemoSection from "@/components/DemoSection";
import { supabase } from "@/lib/supabaseClient";

const features = [
  {
    title: "Lightning fast",
    desc: "Background removal in under 5 seconds per image using AI.",
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  },
  {
    title: "Transparent PNG",
    desc: "Download production-ready transparent PNGs instantly.",
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
      </svg>
    ),
  },
  {
    title: "Flexible plans",
    desc: "Start free with 20 images/month. Upgrade to Pro or Business for more.",
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18" />
      </svg>
    ),
  },
];

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function Home() {
  const router = useRouter();
  const [email, setEmail] = useState<string | undefined>();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        router.replace("/dashboard");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setEmail(session.user.email ?? undefined);
        router.replace("/dashboard");
      } else {
        setEmail(undefined);
      }
    });

    return () => subscription.unsubscribe();
  }, [router]);

  return (
    <>
      <Head>
        <title>ClearCut - Remove Image Backgrounds Instantly</title>
        <meta name="description" content="Remove image backgrounds instantly with AI. Upload a product image, get a clean transparent PNG in seconds. Free tier with 20 images/month." />
      </Head>
      <div className="min-h-screen bg-bg text-white">
        <Navbar email={email} />

        {/* Hero */}
        <main className="relative overflow-hidden">
          {/* Floating checkerboard decoration */}
          <div className="pointer-events-none absolute right-[-5%] top-20 h-64 w-64 rotate-12 opacity-[0.04]">
            <div className="checkerboard h-full w-full" />
          </div>
          <div className="pointer-events-none absolute left-[-3%] bottom-20 h-48 w-48 -rotate-6 opacity-[0.03]">
            <div className="checkerboard h-full w-full" />
          </div>

          {/* Accent glow blob */}
          <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-[500px] w-[800px] rounded-full bg-accent/[0.03] blur-[120px]" />

          <motion.div
            className="relative mx-auto max-w-4xl px-6 pb-32 pt-24 text-center"
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={fadeUp}>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 py-1.5 text-xs font-medium text-muted">
                <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
                Precision background removal
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="mt-8 font-display text-5xl font-extrabold leading-[1.2] tracking-tight sm:text-7xl overflow-visible"
            >
              Remove backgrounds
              <br />
              <span className="text-gradient-accent">in seconds.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-lg text-lg text-muted"
            >
              Upload a product image, get a clean transparent PNG. No design
              skills needed. Start free, upgrade anytime.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mt-10 flex items-center justify-center gap-4"
            >
              <Link href="/login">
                <motion.span
                  whileHover={{ scale: 1.04, boxShadow: "0 0 30px rgba(200,255,0,0.25)" }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-block rounded-lg bg-accent px-7 py-3.5 text-sm font-bold text-black transition-shadow"
                >
                  Get started free
                </motion.span>
              </Link>
              <Link href="/pricing">
                <motion.span
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-block rounded-lg border border-border px-7 py-3.5 text-sm text-muted transition-colors hover:border-border-light hover:text-white"
                >
                  View pricing
                </motion.span>
              </Link>
            </motion.div>

            <DemoSection />

            {/* Diagonal cut divider */}
            <div className="mt-24 h-px w-full bg-gradient-to-r from-transparent via-border-light to-transparent" />

            {/* Feature cards */}
            <motion.div
              className="mt-16 grid gap-5 text-left sm:grid-cols-3"
              variants={stagger}
            >
              {features.map((f) => (
                <motion.div
                  key={f.title}
                  variants={fadeUp}
                  whileHover={{ y: -4, borderColor: "rgba(200,255,0,0.3)" }}
                  className="group rounded-xl border border-border bg-surface/50 p-6 transition-colors"
                >
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    {f.icon}
                  </div>
                  <h3 className="font-display text-sm font-semibold text-white">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">
                    {f.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </main>

        <Footer />
      </div>
    </>
  );
}
